/**
 * PlaceCoreVibeSection — "Så upplevs platsen"
 * Only renders if vibeText >= 80 chars.
 * Now also renders metadata (verified, season badges) as discrete text below vibe.
 * Uses FarmlyMarkdown for safe formatted rendering.
 */

import { FarmlyMarkdown } from "@/components/content/FarmlyMarkdown";
import { useTranslate } from "@/i18n";
import { SEASON_ORDER } from "@/lib/placecore/public/mappings";

type Badge =
  | { type: "season"; seasons: string[] }
  | { type: "duration"; duration: string };

interface Props {
  text: string;
  verified?: boolean;
  badges?: Badge[];
}

export function PlaceCoreVibeSection({ text, verified, badges = [] }: Props) {
  const t = useTranslate();
  const metaItems: string[] = [];
  if (verified) metaItems.push("✔ Verifierad");
  badges.forEach((b) => {
    if (b.type === "season") {
      if (b.seasons.length === 4) {
        metaItems.push(t("place.badge.season.yearRound"));
      } else {
        const sorted = [...b.seasons].sort(
          (a, c) => SEASON_ORDER.indexOf(a as any) - SEASON_ORDER.indexOf(c as any),
        );
        const first = t(`season.${sorted[0]}`);
        const last = t(`season.${sorted[sorted.length - 1]}`);
        const range = sorted.length === 1 ? first : `${first}–${last}`;
        metaItems.push(t("place.badge.season.range", { range }));
      }
    } else {
      metaItems.push(t(`place.badge.duration.${b.duration}`) || b.duration);
    }
  });

  return (
    <section>
      <h2 className="font-display text-lg text-foreground mb-3">
        {t("place.vibe.heading")}
      </h2>
      <FarmlyMarkdown content={text} />
      {metaItems.length > 0 && (
        <p className="mt-5 text-xs text-muted-foreground/60 tracking-wide">
          {metaItems.join("  •  ")}
        </p>
      )}
    </section>
  );
}
